import { formOptions } from '@tanstack/react-form';
import { z } from 'zod';

import { notificationChannelSchema } from '@/entities/notification';
import { SCHEDULE_PERIOD, WEEKDAY } from '@/types/commonConst';
import {
  WORKFLOW_CHANNEL_TYPE,
  WORKFLOW_SUBSCRIPTION_BASIS,
} from '@/types/workflowConst';

// 스케줄 스키마
const scheduleSchema = z.object({
  hour: z.number().min(0).max(23),
  period: z.nativeEnum(SCHEDULE_PERIOD),
  weekday: z.nativeEnum(WEEKDAY).optional(),
  date: z.number().min(1).max(28).optional(),
});

// Subscription 폼 스키마 (backend SubscriptionWorkflowState + base fields 구조와 일치)
export const subscriptionFormSchema = z.object({
  title: z.string().min(1, { message: 'Please enter a title' }),
  channel: notificationChannelSchema,
  // SubscriptionWorkflowState 필드들
  basis: z.literal(WORKFLOW_SUBSCRIPTION_BASIS.DASHBOARD2),
  target: z.string().min(1, { message: 'Please select a dashboard' }),
  schedule: scheduleSchema,
  timezone: z.string().min(1),
});

export type SubscriptionFormData = z.infer<typeof subscriptionFormSchema>;

// 기본값 (timezone은 사용자 시스템 기준)
export const subscriptionFormDefaultValues: SubscriptionFormData = {
  title: '',
  channel: {
    type: WORKFLOW_CHANNEL_TYPE.SLACK,
    slack_webhook_id: '',
  },
  basis: WORKFLOW_SUBSCRIPTION_BASIS.DASHBOARD2,
  target: '',
  schedule: {
    period: SCHEDULE_PERIOD.DAILY,
    hour: 0,
  },
  timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
};

// 폼 검증 함수
const validateSubscriptionForm = ({
  value,
}: {
  value: SubscriptionFormData;
}) => {
  const result = subscriptionFormSchema.safeParse(value);
  if (result.success) return undefined;

  return result.error.issues.map((issue) => ({
    path: issue.path,
    message: issue.message,
  }));
};

export const subscriptionFormOptions = formOptions({
  defaultValues: subscriptionFormDefaultValues,
  validators: {
    onSubmit: validateSubscriptionForm,
  },
});
